import "./styles.css";
// @ts-ignore
import "@fortawesome/fontawesome-free/css/all.min.css";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

// PROJECTS
const projectData = [
  {
    title: "AgriSathi",
    description: "A smart app helping farmers with crop advice, weather updates, and market prices in regional languages.",
    repoUrl: "https://github.com/lobsangshakya/AgriSathi-app",
    tech: ["React", "Supabase", "CNN", "Python"]
  },
  {
    title: "Project Zinova",
    description: "An AI-powered platform that reduces food waste by connecting farmers, restaurants, and NGOs to redistribute surplus food in real time.",
    repoUrl: "https://github.com/lobsangshakya/Zinova-Platform",
    tech: ["React", "Python", "FastAPI", "SMTP", "Gsheet"]
  },
  {
    title: "Eco Smart Bin",
    description: "An AI-driven waste management website promoting cleanliness with smart sorting and an interactive design.",
    repoUrl: "https://github.com/lobsangshakya/Eco_smart_bin",
    tech: ["HTML", "CSS", "JavaScript", "Django"]
  },
  {
    title: "IntelliMove DotCatcher",
    description: "Real-Time Dot Game – click appearing dots; Kafka streams events to track score, misses, and duration.",
    repoUrl: "https://github.com/lobsangshakya/IntelliMove---DotCatcher",
    tech: ["React", "Python", "Kafka", "WebSocket", "Docker"]
  },
  {
    title: "ConvoAI",
    description: "An AI chatbot leveraging reinforcement learning for adaptive responses, fully containerized for easy deployment.",
    repoUrl: "https://github.com/lobsangshakya/ConvoAI",
    tech: ["Python", "AI", "RL", "Docker", "FastAPI"]
  },
  {
    title: "Myob to Xero Conversion",
    description: "A streamlined conversion tool for MMC Company, enabling live file conversions with a user-friendly interface.",
    repoUrl: "https://github.com/lobsangshakya/Myob_to_Xero",
    tech: ["HTML", "CSS", "JavaScript", "Django", "Python"]
  }
];

const socialLinks = [
  { icon: "fa-linkedin", url: "https://www.linkedin.com/in/lobsang-shakya" },
  { icon: "fa-github", url: "https://github.com/lobsangshakya" },
  { icon: "fa-youtube", url: "https://www.youtube.com/@Lotse04" },
];

const fadeIn = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.35 }
};

interface SectionProps {
  setCurrentSection: (sectionId: string) => void;
}

const Navbar: React.FC<SectionProps> = ({ setCurrentSection }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleNavClick = (sectionId: string, e: React.MouseEvent) => {
    e.preventDefault();
    setCurrentSection(sectionId);
    setIsOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <nav className="navbar">
      <a href="#" className="nav-logo" onClick={(e) => handleNavClick("hero", e)}>
        Lobsang S.
      </a>
      <div className={`navbar-links ${isOpen ? "active" : ""}`}>
        <a href="#" onClick={(e) => handleNavClick("hero", e)}>Home</a>
        <a href="#" onClick={(e) => handleNavClick("about", e)}>About</a>
        <a href="#" onClick={(e) => handleNavClick("projects", e)}>Projects</a>
        <a href="#" onClick={(e) => handleNavClick("contacts", e)}>Contact</a>
      </div>
      <button className="hamburger" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle navigation menu">
        <i className={isOpen ? "fas fa-times" : "fas fa-bars"}></i>
      </button>
    </nav>
  );
};

const Hero: React.FC<SectionProps> = ({ setCurrentSection }) => (
  <motion.section className="section" id="hero" {...fadeIn}>
    <div className="content">
      <h1>Welcome to <span>Lobsang's Portfolio</span></h1>
      <p>Innovating across Data, Web and AI</p>
      <p className="location">
        <i className="fas fa-map-marker-alt"></i> Bangalore, India
      </p>
      <button className="btn" onClick={() => setCurrentSection("about")}>
        Discover More <i className="fas fa-arrow-right"></i>
      </button>
    </div>
  </motion.section>
);

const About: React.FC<SectionProps> = ({ setCurrentSection }) => (
  <motion.section className="section" id="about" {...fadeIn}>
    <div className="content">
      <h2>About <span>Me</span></h2>
      <p>
        I'm Lobsang Tsetan Shakya, a Software Engineer Intern. With experience
        as a Full-Stack Engineer / Data Science Intern at MMC Convert and Tech
        member at ANOVA Club, I am passionate about building innovative,
        data-driven solutions that make an impact.
      </p>
      <button className="btn" onClick={() => setCurrentSection("projects")}>
        View My Work
      </button>
    </div>
  </motion.section>
);

const Projects: React.FC<SectionProps> = ({ setCurrentSection }) => (
  <motion.section className="section" id="projects" {...fadeIn}>
    <div className="content">
      <h2>My <span>Projects</span></h2>
      <p>
        Here are a few things I've been working on. For more, check out my{" "}
        <a href="https://github.com/lobsangshakya" target="_blank" rel="noopener noreferrer">
          GitHub
        </a>.
      </p>
      <div className="project-grid">
        {projectData.map((project, index) => (
          <div className="project-card" key={index}>
            <h3>{project.title}</h3>
            <p>{project.description}</p>
            <div className="tech-stack">
              {project.tech.map((tech, techIndex) => (
                <span key={techIndex} className="tech-item">{tech}</span>
              ))}
            </div>
            <a
              href={project.repoUrl}
              className="btn-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              View on GitHub <i className="fa-brands fa-github"></i>
            </a>
          </div>
        ))}
      </div>
      <button className="btn" onClick={() => setCurrentSection("contacts")}>
        Let's Connect
      </button>
    </div>
  </motion.section>
);

const Contacts: React.FC<SectionProps> = ({ setCurrentSection }) => (
  <motion.section className="section" id="contacts" {...fadeIn}>
    <div className="content">
      <h2>Get In <span>Touch</span></h2>
      <p>
        I'm a Software Engineer Intern who is always open to collaborating on meaningful projects and learning from new opportunities.
      </p>
      <div className="contact-info">
        <p><i className="fa-solid fa-envelope"></i> lobsangshakya5@gmail.com</p>
      </div>
      <div className="social-links">
        {socialLinks.map((link, index) => (
          <a
            key={index}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={link.icon}
          >
            <i className={`fa-brands ${link.icon}`}></i>
          </a>
        ))}
      </div>
      <button className="btn" onClick={() => setCurrentSection("hero")}>
        Back to Home
      </button>
    </div>
  </motion.section>
);

const LightModeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.106a.75.75 0 010 1.06l-1.591 1.59a.75.75 0 11-1.06-1.06l1.59-1.59a.75.75 0 011.06 0zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.894 17.894a.75.75 0 011.06 0l1.59 1.59a.75.75 0 11-1.06 1.06l-1.59-1.59a.75.75 0 010-1.06zM12 17.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 17.25zM6.106 18.894a.75.75 0 010-1.06l1.59-1.59a.75.75 0 111.06 1.06l-1.59 1.59a.75.75 0 01-1.06 0zM4.5 12a.75.75 0 01-.75.75H1.5a.75.75 0 010-1.5h2.25A.75.75 0 014.5 12zM6.106 5.106a.75.75 0 011.06 0l1.591 1.59a.75.75 0 01-1.06 1.06L6.106 6.166a.75.75 0 010-1.06z" />
  </svg>
);

const DarkModeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path fillRule="evenodd" d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 004.463-.949a.75.75 0 01.819.162l.805.806a.75.75 0 01-.162.819A10.5 10.5 0 119.528 1.718zM16.5 9a.75.75 0 000-1.5h-3a.75.75 0 000 1.5h3z" clipRule="evenodd" />
  </svg>
);

export const App: React.FC = () => {
  const [currentSection, setCurrentSection] = useState<string>("hero");
  const [isDarkMode, setIsDarkMode] = useState<boolean>(true);

  const handleSectionChange = (sectionId: string) => {
    if (sectionId === currentSection) return;
    setCurrentSection(sectionId);
  };

  useEffect(() => {
    document.body.className = isDarkMode ? "dark-mode" : "light-mode";
  }, [isDarkMode]);

  const renderSection = () => {
    switch (currentSection) {
      case "hero":     return <Hero     setCurrentSection={handleSectionChange} />;
      case "about":    return <About    setCurrentSection={handleSectionChange} />;
      case "projects": return <Projects setCurrentSection={handleSectionChange} />;
      case "contacts": return <Contacts setCurrentSection={handleSectionChange} />;
      default:         return <Hero     setCurrentSection={handleSectionChange} />;
    }
  };

  return (
    <>
      <Navbar setCurrentSection={handleSectionChange} />
      <main>
        <AnimatePresence mode="wait">
          {renderSection()}
        </AnimatePresence>
      </main>
      <div className="mode-toggle">
        <button onClick={() => setIsDarkMode(false)} aria-label="Switch to light mode">
          <LightModeIcon />
        </button>
        <button onClick={() => setIsDarkMode(true)} aria-label="Switch to dark mode">
          <DarkModeIcon />
        </button>
      </div>
    </>
  );
};

export default App;
